import { TFunction } from 'i18next';

export const ROLE_KEY_BY_PROVIDER_TEXT: Record<string, string> = {
  styrelseledamot: 'company.roles.boardMember',
  ordförande: 'company.roles.chairman',
  styrelseordförande: 'company.roles.chairman',
  styrelsesuppleant: 'company.roles.deputyBoardMember',
  suppleant: 'company.roles.deputy',
  arbetstagarrepresentant: 'company.roles.employeeRep',
  vd: 'company.roles.ceo',
  'verkställande direktör': 'company.roles.ceo',
  'vice vd': 'company.roles.deputyCeo',
  ledamot: 'company.roles.member',
  'extern firmatecknare': 'company.roles.externalSignatory',
  'extern vd': 'company.roles.externalCeo',
  bolagsman: 'company.roles.partner',
  komplementär: 'company.roles.generalPartner',
  likvidator: 'company.roles.liquidator',
  'actual member of board': 'company.roles.boardMember',
  'member of board': 'company.roles.boardMember',
  'chairman of board': 'company.roles.chairman',
  chairman: 'company.roles.chairman',
  'managing director': 'company.roles.ceo',
  'deputy managing director': 'company.roles.deputyCeo',
  'deputy member of board': 'company.roles.deputyBoardMember',
  'employee representative': 'company.roles.employeeRep',
  substitute: 'company.roles.deputy',
};

export const translateProviderRole = (role: string, t: TFunction): string =>
  (role || '')
    .split(',')
    .map(token => token.trim())
    .filter(Boolean)
    .map(token => {
      const key = ROLE_KEY_BY_PROVIDER_TEXT[token.toLowerCase()];
      return key ? t(key, token) : token;
    })
    .join(', ');

// Bolagsverket function codes (from the Syna adapter's FUNCTION_LABEL_FALLBACKS and the codes
// creditsafeRegistry.js maps its own vocabulary to), each with its translation key and the
// Swedish label to fall back to while that key is unresolved in Lokalise.
const FUNCTION_CODE_ROLE: Record<string, { key: string; fallback: string }> = {
  LE: { key: 'company.roles.boardMember', fallback: 'Styrelseledamot' },
  OF: { key: 'company.roles.chairman', fallback: 'Ordförande' },
  SU: { key: 'company.roles.deputyBoardMember', fallback: 'Styrelsesuppleant' },
  VD: { key: 'company.roles.ceo', fallback: 'Verkställande direktör' },
  VVD: { key: 'company.roles.deputyCeo', fallback: 'Vice verkställande direktör' },
  EVD: { key: 'company.roles.externalCeo', fallback: 'Extern VD' },
  EFT: { key: 'company.roles.externalSignatory', fallback: 'Extern firmatecknare' },
  ARB: { key: 'company.roles.employeeRep', fallback: 'Arbetstagarrepresentant' },
  BO: { key: 'company.roles.partner', fallback: 'Bolagsman' },
  KP: { key: 'company.roles.generalPartner', fallback: 'Komplementär' },
  LIKV: { key: 'company.roles.liquidator', fallback: 'Likvidator' },
};

/**
 * Translates a registry person's role. Each function code lines up with the same-position segment
 * of `roleText` (both describe the same role, split the same way), so a code maps only its own
 * segment and an unmapped segment keeps its free text instead of losing the role. Creditsafe can
 * drop unmapped positions from `functions` while keeping every segment in `roleText`, so once the
 * counts disagree there is no reliable pairing left and the whole text goes through the free-text
 * table instead.
 */
export const translateSignatoryRole = (
  person: { functions?: string[]; roleText: string },
  t: TFunction,
): string => {
  const functions = person.functions || [];
  const segments = (person.roleText || '')
    .split(', ')
    .map(segment => segment.trim())
    .filter(Boolean);

  if (functions.length !== segments.length) return translateProviderRole(person.roleText, t);

  return segments
    .map((segment, index) => {
      const role = FUNCTION_CODE_ROLE[functions[index]];
      return role ? t(role.key, role.fallback) : translateProviderRole(segment, t);
    })
    .join(', ');
};
